import React, { useState, useEffect, useContext, Fragment } from 'react';
import {
    Row,
    Col,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    CustomInput,
    FormGroup
} from 'reactstrap';
import { useForm } from 'react-hook-form';
import CountUp from 'react-countup';
import Flatpickr from 'react-flatpickr';
import DetailRow from '../../components/GestionPersonaEmpresa/personDetails/DetailRow';
import WizardInput from '../../components/auth/wizard/WizardInput';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import paymentsType from '../../data/recaudo/paymentsType';
import { EnvironmentContext } from '../../context/Context';
import { generalServices } from '../../services';
import { currencyFormat } from '../../helpers/tableFormatters';
import { currencyFormat as currencyNumberFormat } from '../../helpers/formatters';
import { getTodayStr } from '../../helpers/dateFormatters';
import { getLocaleCalendarConfig } from '../../helpers/utils';

function ModalPayments({ open, toggle, title, data, onSubmit, onOk, textBtn, egreso, solicitudes, fecha, setFecha }) {
    const environment = useContext(EnvironmentContext);
    const { register, setValue, getValues, errors, watch } = useForm({
        defaultValues: {
            paymentType: 1
        }
    });

    const watchPaymentType = watch("paymentType");
    const [treasuries, setTreasuries] = useState([]);
    const [destAccounts, setDestAccounts] = useState([]);
    const [banks, setBanks] = useState([]);
    const [cashConcepts, setCashConcepts] = useState([]);
    const [requiereSolicitud, setRequiereSolicitud] = useState(false);
    const [solicitud, setSolicitud] = useState({});
    // Lo que aún se puede anticipar sobre la solicitud elegida
    const disponible = (solicitud?.monto || 0) - (solicitud?.anticipos || 0);

    useEffect(() => {
        loadCashConcepts();
    }, [])

    // El efectivo se mueve siempre con la fecha del día; al volver a efectivo se restablece
    useEffect(() => {
        if (watchPaymentType != 1) return;
        const hoy = getTodayStr();
        if (fecha != hoy) setFecha(hoy);
    }, [watchPaymentType])

    useEffect(() => {
        loadBanks();
        loadTreasuries();
        loadBankAccounts();
    }, [environment])

    const loadTreasuries = async () => {
        if (!environment.treasuries) return;
        const tempTreasuries = environment.treasuries.map(treasury => ({
            value: treasury.tesoreria,
            label: treasury.nombre
        }))
        setTreasuries(tempTreasuries);
    }

    const loadBankAccounts = async () => {
        if (!environment.bankAccounts) return;
        const tempDestAccounts = environment.bankAccounts.map(account => ({
            value: account.idCuenta,
            label: `${account.nroCuenta} - ${account.nombreBanco}`
        }))

        setDestAccounts(tempDestAccounts);
    }

    const loadBanks = async () => {
        if (!environment.banks) return;
        const tempBanks = environment.banks.map(bank => ({
            value: bank.id,
            label: bank.nombre
        }))
        setBanks(tempBanks);
    }

    const loadCashConcepts = async () => {
        if (cashConcepts && cashConcepts[0]) return;
        const types = egreso ? "T/C" : "T/D"
        const res = await generalServices.getCustomConcepts(types);
        if (res.error || !res.objResponse) return;

        const tmpConcepts = res.objResponse.map(concept => ({
            value: concept.conceptotesoreria,
            label: concept.nombre,
        }))

        setCashConcepts(tmpConcepts);
    }

    const onChange = async e => {
        let { name, value } = e;
        setValue(name, value);
        if (name == "cashConcept") {
            setRequiereSolicitud(value == "11");
        } else if (name == "idMovimiento") {
            setSolicitud(solicitudes.find(solicitud => solicitud.value == value));
        }
    }

    const handleSubmit = async () => {
        const generalData = getValues();

        if (!generalData.cashConcept) {
            cogoToast.error("Por favor complete los campos requeridos",
                { position: 'bottom-left', heading: 'Campos requeridos' });
            return;
        }

        if (watchPaymentType == 1 && !generalData.cashSource) {
            cogoToast.error("Por favor complete los campos requeridos para efectivo",
                { position: 'bottom-left', heading: 'Campos requeridos' });
            return;
        }

        if (watchPaymentType == 2 && !generalData.destAccount) {
            cogoToast.error("Por favor selecciona una cuenta de destino",
                { position: 'bottom-left', heading: 'Campos requeridos' });
            return;
        }

        if (watchPaymentType == 3 && (!generalData.chequeBank || !generalData.numCheque)) {
            cogoToast.error("Por favor completa la información del cheque",
                { position: 'bottom-left', heading: 'Campos requeridos' });
            return;
        }

        if (!fecha) {
            cogoToast.error("Por favor indica la fecha del movimiento",
                { position: 'bottom-left', heading: 'Campos requeridos' });
            return;
        }

        if (generalData.cashConcept == 11 && !generalData.idMovimiento) {
            cogoToast.error("Por favor selecciona una solicitud",
                { position: 'bottom-left', heading: 'Campos requeridos' });
            return;
        }

        // Los anticipos se descuentan al desembolsar: no pueden superar el monto de la solicitud.
        // La base lo vuelve a validar; esto solo avisa antes de enviar.
        if (generalData.cashConcept == 11 && solicitud?.value && Number(data.total) > disponible) {
            cogoToast.error(`El anticipo supera el disponible de la solicitud: ${currencyNumberFormat(disponible) || '$ 0'}`,
                { position: 'bottom-left', heading: 'Anticipo' });
            return;
        }

        if (egreso && watchPaymentType == 1) {
            var saldoCaja = await generalServices.getSaldoCaja();
            if (saldoCaja < data.total) {
                cogoToast.error("Saldo insuficiente en caja " + saldoCaja + " para pagar " + data.total,
                    { position: 'bottom-left', heading: 'Saldo insuficiente' });
                return;
            }
        }

        const payload = {
            modalidadPago: watchPaymentType,
            idTesoreria: generalData.cashSource ? Number(generalData.cashSource) : 0,
            conceptoTesoreria: generalData.cashConcept ? Number(generalData.cashConcept) : 0,
            idCuentaDestino: generalData.destAccount ? Number(generalData.destAccount) : 0,
            idBancoCheque: generalData.chequeBank ? Number(generalData.chequeBank) : 0,
            numeroCheque: generalData.numCheque || '',
            observacion: generalData.observations || '',
            idMovimiento: generalData.cashConcept == 11 && generalData.idMovimiento || 0,
            idCredit: generalData.cashConcept == 11 && solicitud?.label || ""
        }

        onSubmit({ ...payload });
    }

    return (
        <Modal size="lg" isOpen={open}>
            <ModalHeader
                close={
                    <div className='d-flex justify-content-center'>
                        <FontAwesomeIcon
                            onClick={() => toggle(false)}
                            style={{ cursor: 'pointer' }}
                            size='lg'
                            icon="times"
                        />
                    </div>}
            >
                <h5>{title}</h5>
            </ModalHeader>
            <ModalBody className="p-0">
                <form className='p-3'>
                    <Row className="m-0 w-100 justify-content-between p-2 rounded bg-light px-3">
                        {paymentsType && paymentsType.map(paymentType => (
                            <FormGroup
                                tag={CustomInput}
                                type="radio"
                                id={paymentType.name}
                                name="paymentType"
                                label={<strong>{paymentType.name}</strong>}
                                value={paymentType.id}
                                className="m-0"
                                innerRef={register({
                                    required: true,
                                })}
                                checked={watchPaymentType == paymentType.id}
                                onChange={({ target }) => onChange(target)}
                            >
                            </FormGroup>
                        ))}
                    </Row>
                    <DetailRow title="Fecha Movimiento" className="m-0 mt-4">
                        <FormGroup>
                            <Flatpickr
                                className="form-control pl-2"
                                placeholder='aaaa/mm/dd'
                                options={{
                                    dateFormat: "Y/m/d",
                                    locale: getLocaleCalendarConfig(),
                                }}
                                value={fecha ? fecha.replaceAll("-", "/") : undefined}
                                onChange={(date, dateStr) => setFecha(dateStr.replaceAll("/", "-"))}
                                // En efectivo la fecha es la del día; consignación y cheque pueden cambiarla
                                disabled={watchPaymentType == 1}
                            />
                        </FormGroup>
                    </DetailRow>
                    {watchPaymentType && watchPaymentType == 1 && (
                        <Fragment>
                            <DetailRow title="Tesorería" className="m-0 mt-4">
                                <FormGroup>
                                    <WizardInput
                                        className="pl-2"
                                        type="select"
                                        tag={CustomInput}
                                        placeholder="Seleccionar"
                                        id="cashSource"
                                        name="cashSource"
                                        options={treasuries}
                                        onChange={(e) => onChange({ name: 'cashSource', value: e.target.value })}
                                        errors={errors}
                                        innerRef={register({
                                            required: true,
                                        })}
                                    />
                                </FormGroup>
                            </DetailRow>
                        </Fragment>
                    )}

                    {watchPaymentType && watchPaymentType == 2 && (
                        <>
                            <DetailRow title="Cuenta y Banco Destino" className="m-0 mt-4">
                                <FormGroup>
                                    <WizardInput
                                        className="pl-2"
                                        type="select"
                                        tag={CustomInput}
                                        placeholder="Seleccionar"
                                        id="destAccount"
                                        name="destAccount"
                                        options={destAccounts}
                                        onChange={(e) => onChange({ name: 'destAccount', value: e.target.value })}
                                        errors={errors}
                                        innerRef={register({
                                            required: true,
                                        })}
                                    />
                                </FormGroup>
                            </DetailRow>
                        </>
                    )}
                    {watchPaymentType && watchPaymentType == 3 && (
                        <>
                            <DetailRow title="Banco" className="m-0 mt-4">
                                <FormGroup>
                                    <WizardInput
                                        className="pl-2"
                                        type="select"
                                        tag={CustomInput}
                                        placeholder="Seleccionar"
                                        id="chequeBank"
                                        name="chequeBank"
                                        options={banks}
                                        onChange={(e) => onChange({ name: 'chequeBank', value: e.target.value })}
                                        errors={errors}
                                        innerRef={register({
                                            required: true,
                                        })}
                                    />
                                </FormGroup>
                            </DetailRow>
                            <DetailRow title="Numero del Cheque" className="m-0">
                                <FormGroup>
                                    <Input
                                        id="numCheque"
                                        name="numCheque"
                                        onChange={(e) => onChange({ name: 'numCheque', value: e.target.value }, true)}
                                        {...register("numCheque", {
                                            required: true,
                                        })}
                                    />
                                </FormGroup>
                            </DetailRow>
                        </>
                    )}
                    <DetailRow title="Concepto" className="m-0">
                        <FormGroup>
                            <WizardInput
                                className="pl-2"
                                type="select"
                                tag={CustomInput}
                                placeholder="Seleccionar"
                                id="cashConcept"
                                name="cashConcept"
                                options={cashConcepts}
                                onChange={(e) => onChange({ name: 'cashConcept', value: e.target.value })}
                                errors={errors}
                                innerRef={register({
                                    required: true,
                                })}
                            />
                        </FormGroup>
                    </DetailRow>
                    {requiereSolicitud && (
                        <DetailRow title="Solicitud" className="m-0">
                            <FormGroup>
                                <WizardInput
                                    className="pl-2"
                                    type="select"
                                    tag={CustomInput}
                                    placeholder="Seleccionar"
                                    id="idMovimiento"
                                    name="idMovimiento"
                                    // el label sigue siendo el número de crédito (se envía como idCredit); aquí solo se muestra más
                                    options={solicitudes.map(s => ({ ...s, label: [s.label, s.cedula, s.nombre].filter(Boolean).join(' · ') }))}
                                    onChange={(e) => onChange({ name: 'idMovimiento', value: e.target.value })}
                                    errors={errors}
                                    innerRef={register({
                                        required: true,
                                    })}
                                />
                            </FormGroup>
                            {solicitud && <Row>
                                <Col><span className="fs--2">Número:</span> {solicitud.value}</Col>
                                <Col><span className="fs--2">Fecha:</span> {solicitud.fecha}</Col>
                                <Col><span className="fs--2">Monto:</span> {currencyFormat(solicitud.monto)}</Col>
                            </Row>}
                            {solicitud?.value && <Row>
                                <Col><span className="fs--2">Solicitante:</span> {solicitud.cedula} · {solicitud.nombre}</Col>
                            </Row>}
                            {solicitud?.value && <Row>
                                <Col><span className="fs--2">Anticipado:</span> {currencyFormat(solicitud.anticipos || 0)}</Col>
                                <Col><span className="fs--2">Disponible:</span> {currencyFormat(disponible)}</Col>
                                <Col></Col>
                            </Row>}
                        </DetailRow>)}
                    <DetailRow title="Observaciones" className="mt-3 mx-2">
                        <FormGroup>
                            <Input
                                type="textarea"
                                id="observations"
                                maxLength={300}
                                onChange={(e) => onChange({ name: 'observations', value: e.target.value })}
                                name="observations"
                                {...register("observations", {
                                    required: false,
                                })}
                            />
                        </FormGroup>
                    </DetailRow>
                </form>

            </ModalBody>
            <ModalFooter className="no-border">
                <>
                    <Col >
                        <div className='w-100 d-flex flex-row justify-content-between'>
                            <DetailRow title="Total" className="mb-1">
                                <div className='display-4 fs-4 font-weight-normal text-sans-serif text-info'>
                                    <CountUp end={data.total} duration={1} prefix="$" separator="." decimal="," decimals={2} />
                                </div>
                            </DetailRow>
                        </div>
                    </Col>
                    <Col className="text-right" >
                        <Button onClick={() => toggle()} className="mr-2" transform="shrink-3 down-2" color="falcon-default" >
                            Cancelar
                        </Button>
                        <Button onClick={handleSubmit} transform="shrink-3 down-2" color="primary" >
                            {textBtn}
                        </Button>
                    </Col>
                </>
            </ModalFooter>
        </Modal>
    )
}

export default ModalPayments

